/**
 * Post Recording Review Component
 *
 * Allows users to review their recorded video and choose to re-record or continue to upload
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Replay, CloudUpload } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../../../common/recordingUtilities';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(3),
    maxWidth: 800,
    margin: '0 auto'
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: 640,
    margin: '0 auto',
    backgroundColor: '#000',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    marginBottom: theme.spacing(2)
  },
  video: {
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  infoContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1)
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0
    }
  },
  infoLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  infoValue: {
    color: theme.palette.text.primary
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2)
  },
  button: {
    minWidth: 160,
    padding: theme.spacing(1.5, 3)
  }
}));

interface PostRecordingReviewProps {
  recordedBlob: Blob;
  recordingDuration: number;
  onReRecord: () => void;
  onContinue: (blob: Blob) => void;
}

export default function PostRecordingReview({
  recordedBlob,
  recordingDuration,
  onReRecord,
  onContinue
}: PostRecordingReviewProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');

  // Create object URL for video preview
  useEffect(() => {
    if (recordedBlob) {
      const url = URL.createObjectURL(recordedBlob);
      setVideoUrl(url);

      // Cleanup
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [recordedBlob]);

  // Set video source
  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.src = videoUrl;
    }
  }, [videoUrl]);

  const handleContinue = () => {
    onContinue(recordedBlob);
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper}>
        <Typography variant="h5" className={classes.title}>
          {t('review.title', {ns: 'guidedrecording'})}
        </Typography>

        {/* Video Preview */}
        <Box className={classes.videoContainer}>
          <video
            ref={videoRef}
            className={classes.video}
            controls
            playsInline
          />
        </Box>

        {/* Recording Info */}
        <Box className={classes.infoContainer}>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              {t('review.duration', {ns: 'guidedrecording'})}
            </Typography>
            <Typography className={classes.infoValue}>
              {formatDuration(recordingDuration)}
            </Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              File Size
            </Typography>
            <Typography className={classes.infoValue}>
              {formatFileSize(recordedBlob.size)}
            </Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              Format
            </Typography>
            <Typography className={classes.infoValue}>
              {recordedBlob.type}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box className={classes.buttonContainer}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            className={classes.button}
            onClick={onReRecord}
            startIcon={<Replay />}
          >
            {t('review.rerecord', {ns: 'guidedrecording'})}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.button}
            onClick={handleContinue}
            startIcon={<CloudUpload />}
          >
            {t('review.continue', {ns: 'guidedrecording'})}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
